import { ProseWrapper } from "@libs/zapi-ui/custom";
import { useEffect, useState, useCallback } from "react";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import { createEditorExtensionsWithDiff } from "@/zapi-frontend/widgets/TipTapEditor/utils/editor-extensions";
import { useParams } from "react-router-dom";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useDiffStore } from "@/zapi-frontend/stores/diff";
import { useDebounce } from "@/zapi-frontend/shared/utils/useDebounce";
import { hasPendingDiffs } from "@/zapi-frontend/utils/diff-utils";
import { cn } from "@/zapi-frontend/shared/lib/utils";

function applyCropDataToImages(editor: Editor) {
  const currentChapter = useChapterStore.getState().activeChapter;
  if (!currentChapter?.activeMemo?.croppedMemoImages) return;

  const croppedImages = [...currentChapter.activeMemo.croppedMemoImages];
  const imageLoadPromises: Promise<{ pos: number; attrs: Record<string, unknown> }>[] = [];

  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === "image") {
      const imageUrl = node.attrs.src;
      const cropDataIndex = croppedImages.findIndex((img) => img.imageUrl === imageUrl);

      if (cropDataIndex !== -1) {
        const cropData = croppedImages[cropDataIndex];
        croppedImages.splice(cropDataIndex, 1);

        imageLoadPromises.push(
          new Promise((resolve) => {
            const img = new window.Image();
            img.onload = () => {
              resolve({
                pos,
                attrs: {
                  ...node.attrs,
                  imageId: cropData.id,
                  tlx: cropData.tlCoordinateX,
                  tly: cropData.tlCoordinateY,
                  brx: cropData.brCoordinateX,
                  bry: cropData.brCoordinateY,
                  w: img.naturalWidth,
                  h: img.naturalHeight
                }
              });
            };
            img.onerror = () =>
              resolve({
                pos,
                attrs: node.attrs
              });
            img.src = imageUrl;
          })
        );
      }
    }
  });

  if (imageLoadPromises.length > 0) {
    Promise.all(imageLoadPromises).then((changes) => {
      const validChanges = changes.filter((c) => c.attrs.tlx);
      if (validChanges.length > 0) {
        editor.commands.command(({ tr }) => {
          for (const { pos, attrs } of validChanges) {
            tr.setNodeMarkup(pos, undefined, attrs);
          }
          return true;
        });
      }
    });
  }
}

export function HighlightedDiff2({ diffDoneCallback, className }: { diffDoneCallback: (content: string) => void; className?: string }) {
  const { id } = useParams();
  const projectId = typeof id === "string" ? id : "";
  const { activeChapter } = useChapterStore();
  const chapterId = activeChapter?.id?.toString() || "";

  const { getDiffEditor, saveDiffEditor, acceptChanges } = useDiffStore();
  const [htmlContent, setHtmlContent] = useState(() => getDiffEditor(projectId, chapterId)?.htmlContent ?? "");

  useEffect(() => {
    const diffEditor = getDiffEditor(projectId, chapterId);
    if (diffEditor) {
      setHtmlContent(diffEditor.htmlContent);
    }
  }, [getDiffEditor, projectId, chapterId]);

  const saveToStore = useCallback(
    (html: string) => {
      saveDiffEditor(projectId, chapterId, html);
    },
    [projectId, chapterId, saveDiffEditor]
  );

  const debouncedSave = useDebounce(saveToStore, 500);

  const editor = useEditor({
    editable: true,
    extensions: createEditorExtensionsWithDiff(),
    content: htmlContent || "",
    onUpdate: ({ editor }) => {
      const updatedHtml = editor.getHTML();
      if (hasPendingDiffs(updatedHtml)) {
        debouncedSave(updatedHtml);
      } else {
        acceptChanges(projectId, chapterId, diffDoneCallback);
      }
    }
  });

  useEffect(() => {
    if (editor && htmlContent) {
      editor.chain().setMeta("addToHistory", false).setContent(htmlContent, false).run();
      applyCropDataToImages(editor);
    }
  }, [htmlContent, editor]);

  if (!htmlContent) return null;

  return (
    <ProseWrapper className={cn("max-h-[calc(100vh-124px-56px-40px-48px)] p-2 focus-visible:outline-none [&_.ProseMirror]:focus:outline-none [&_.ProseMirror-focused]:outline-none text-zinc-700 [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-gray-400 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0", className)}>
      <div className="ProseMirror prose prose-lg ">
        <EditorContent editor={editor} />
      </div>
    </ProseWrapper>
  );
}
